import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FileUploadCard } from "@/components/upload/file-upload-card";
import { MediaPreviewCard } from "@/components/upload/media-preview-card";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  updateSupabaseDraft,
  updateSupabaseUpload,
  deleteSupabaseDraft,
} from "@/lib/supabaseDrafts";
import { updateYouTubeMetadata } from "@/lib/youtube";
import { useAuth } from "@/features/auth/auth-context";
import { uploadToCloudinary } from "@/lib/uploadToCloudinary";
import { convertToVideo } from "@/lib/convertToVideo";

export function EditDraftModal({
  open,
  onOpenChange,
  draftToEdit,
  onDraftUpdated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draftToEdit: any;
  onDraftUpdated?: () => void;
}) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<any>({});
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [publishNow, setPublishNow] = useState(false);
  const [uploadMode, setUploadMode] = useState<"audio-thumbnail" | "video">(
    "video"
  );
  const [audioSrc, setAudioSrc] = useState("");
  const [videoSrc, setVideoSrc] = useState("");
  const [imageSrc, setImageSrc] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);

  // ✅ Prefill modal data on open
  useEffect(() => {
    if (open && draftToEdit) {
      setFormData(draftToEdit);

      if (draftToEdit.mediaType === "mp3") {
        setUploadMode("audio-thumbnail");
      } else {
        setUploadMode("video");
      }

      if (draftToEdit.mediaUrl) {
        draftToEdit.mediaType === "mp3"
          ? setAudioSrc(draftToEdit.mediaUrl)
          : setVideoSrc(draftToEdit.mediaUrl);
      }

      if (draftToEdit.customThumbnail || draftToEdit.customthumbnail) {
        setImageSrc(draftToEdit.customThumbnail || draftToEdit.customthumbnail);
      }

      if (draftToEdit.scheduledAt) {
        const date = new Date(draftToEdit.scheduledAt);
        setScheduledTime(format(date, "yyyy-MM-dd'T'HH:mm"));
      }

      if (draftToEdit.status === "published") {
        setPublishNow(true);
      }
    }
  }, [open, draftToEdit]);

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleReplaceMedia = async (file: File) => {
    if (!user || !draftToEdit) return;
    try {
      setIsSaving(true);
      const url = await uploadToCloudinary(file, user.id);
      const mediaType = file.type.startsWith("audio") ? "mp3" : "mp4";

      await updateSupabaseDraft(draftToEdit.id, {
        media_url: url,
        media_type: mediaType,
      });

      if (mediaType === "mp3") {
        setAudioSrc(url);
        setUploadMode("audio-thumbnail");
      } else {
        setVideoSrc(url);
        setUploadMode("video");
      }

      toast.success("✅ Media replaced successfully");
    } catch (err) {
      console.error("❌ Media replace failed", err);
      toast.error("Failed to replace media");
    } finally {
      setIsSaving(false);
    }
  };

  const handleReplaceThumbnail = async (file: File) => {
    if (!user || !draftToEdit) return;
    try {
      setIsSaving(true);
      const url = await uploadToCloudinary(file, user.id);

      await updateSupabaseDraft(draftToEdit.id, {
        customthumbnail: url,
      });

      setImageSrc(url);
      toast.success("✅ Thumbnail replaced successfully");
    } catch (err) {
      console.error("❌ Thumbnail replace failed", err);
      toast.error("Failed to replace thumbnail");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!user || !draftToEdit) {
      toast.error("Missing user or draft ID");
      return;
    }

    const title = formData.title || "";
    const description = formData.description || "";
    const scheduled_time = publishNow ? null : scheduledTime;
    const status = publishNow
      ? "published"
      : scheduled_time
      ? "scheduled"
      : "draft";

    try {
      setIsSaving(true);

      if (draftToEdit?.youtube_video_id) {
        await updateYouTubeMetadata({
          userId: user.id,
          videoId: draftToEdit.youtube_video_id,
          title,
          description,
          scheduledTime: scheduled_time,
          publishNow,
        });

        await updateSupabaseUpload(draftToEdit.youtube_video_id, {
          title,
          description,
          customthumbnail: imageSrc,
          scheduled_time,
          status,
        });
      } else {
        await updateSupabaseDraft(draftToEdit.id, {
          title,
          description,
          media_url: uploadMode === "video" ? videoSrc : audioSrc,
          media_type: uploadMode === "video" ? "mp4" : "mp3",
          customthumbnail: imageSrc,
          scheduled_at: scheduled_time,
          status,
        });
      }

      toast.success("✅ Changes saved successfully");
      onDraftUpdated?.();
      onOpenChange(false);
    } catch (err) {
      console.error("❌ Error saving changes", err);
      toast.error("Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublishNow = async () => {
    if (!user || !draftToEdit) {
      toast.error("Missing user or draft");
      return;
    }

    try {
      setIsPublishing(true);
      setConversionProgress(0);

      let fileUrl = videoSrc;

      // ✅ If video is missing, convert audio + image into video
      if (!videoSrc && audioSrc && imageSrc) {
        setIsConverting(true);
        const videoBlob = await convertToVideo(audioSrc, imageSrc, (p) =>
          setConversionProgress(p)
        );
        const convertedFile = new File([videoBlob], "converted.mp4", {
          type: "video/mp4",
        });
        fileUrl = await uploadToCloudinary(convertedFile, user.id);
        setIsConverting(false);
      }

      const payload = {
        userId: user.id,
        fileUrl,
        metadata: {
          title: formData.title,
          description: formData.description,
          tags: formData.tags || [],
          privacyStatus: "public",
          scheduledTime: scheduledTime || null,
          thumbnailUrl: imageSrc || null,
        },
      };

    const uploadUrl = import.meta.env.DEV
  ? 'http://localhost:5002/youtube-upload'
  : 'https://fortuna-v0-mxmd3e24x-fortunaios-projects.vercel.app/youtube-upload';

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message);

      // ✅ Delete draft after successful publish
      await deleteSupabaseDraft(draftToEdit.id);

      toast.success("✅ Published to YouTube successfully!");
      onDraftUpdated?.();
      onOpenChange(false);
    } catch (err: any) {
      console.error("❌ Publish error", err);
      toast.error(err.message || "Failed to publish draft.");
    } finally {
      setIsPublishing(false);
      setIsConverting(false);
    }
  };

  if (!draftToEdit) return null;

 return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-sm sm:max-w-md md:max-w-3xl lg:max-w-5xl h-[95vh] max-h-[800px] flex flex-col overflow-hidden px-4 sm:px-6 rounded-lg">
        {/* Fixed Header */}
        <div className="flex-shrink-0 pb-4 border-b border-border/20">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            Edit Draft Details
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Update your media, title, description, or publishing options.
          </DialogDescription>

          {/* Upload Mode Tabs */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={uploadMode === "audio-thumbnail" ? "default" : "ghost"}
              onClick={() => setUploadMode("audio-thumbnail")}
              className="text-xs sm:text-sm px-2 sm:px-3"
            >
              Audio + Thumbnail
            </Button>
            <Button
              variant={uploadMode === "video" ? "default" : "ghost"}
              onClick={() => setUploadMode("video")}
              className="text-xs sm:text-sm px-2 sm:px-3"
            >
              Video
            </Button>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-hidden min-h-0 py-4">
          <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 lg:gap-6 h-full">
            {/* LEFT - Form Section */}
            <div className="flex flex-col gap-3 sm:gap-4 overflow-y-auto pr-1 min-h-0">
              {uploadMode === "audio-thumbnail" ? (
                <>
                  <FileUploadCard
                    label="Audio File"
                    fileName={audioSrc ? "Audio Uploaded" : ""}
                    onUpload={handleReplaceMedia}
                    accept="audio/mp3"
                    validated={!!audioSrc}
                  />
                  <FileUploadCard
                    label="Thumbnail Image"
                    fileName={imageSrc ? "Thumbnail Uploaded" : ""}
                    onUpload={handleReplaceThumbnail}
                    accept="image/*"
                    validated={!!imageSrc}
                  />
                </>
              ) : (
                <FileUploadCard
                  label="Video File"
                  fileName={videoSrc ? "Video Uploaded" : ""}
                  onUpload={handleReplaceMedia}
                  accept="video/mp4"
                  validated={!!videoSrc}
                />
              )}

              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">Beat Title</Label>
                <Input
                  id="title"
                  value={formData.title || ""}
                  onChange={(e) => handleChange("title", e.target.value)}
                  className="text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ""}
                  onChange={(e) => handleChange("description", e.target.value)}
                  className="min-h-[6rem] sm:min-h-[8rem] text-sm resize-none"
                />
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Publishing Options</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="publish-now"
                      checked={publishNow}
                      onChange={() => setPublishNow(true)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="publish-now" className="text-sm">Publish Now</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="schedule-later"
                      checked={!publishNow}
                      onChange={() => setPublishNow(false)}
                      className="w-4 h-4"
                    />
                    <label htmlFor="schedule-later" className="text-sm">Schedule for Later</label>
                  </div>
                  {!publishNow && (
                    <Input
                      type="datetime-local"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="text-sm mt-2"
                    />
                  )}
                </div>
              </div>

              {isConverting && (
                <div className="mt-4 p-3 bg-muted/50 rounded-md">
                  <div className="text-sm text-muted-foreground">
                    Converting Audio → Video... {conversionProgress}%
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT - Preview Section */}
            <div className="order-first lg:order-last">
              <div className="sticky top-0 h-fit max-h-[400px] lg:max-h-full">
                <MediaPreviewCard
                  audioSrc={audioSrc}
                  videoSrc={videoSrc}
                  imageSrc={imageSrc}
                  title={formData.title}
                  description={formData.description}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="flex-shrink-0 pt-4 border-t border-border/20">
          <div className="flex flex-col sm:flex-row justify-end gap-2">
            <Button 
              variant="ghost" 
              onClick={() => onOpenChange(false)}
              className="w-full sm:w-auto order-last sm:order-first"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveChanges} 
              disabled={isSaving}
              className="w-full sm:w-auto"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
            {!draftToEdit?.youtube_video_id && (
              <Button
                onClick={handlePublishNow}
                disabled={isPublishing || isConverting}
                className="w-full sm:w-auto"
              >
                {isConverting
                  ? `Converting... (${conversionProgress}%)`
                  : isPublishing
                  ? "Publishing..."
                  : "Publish to YouTube"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
